import { DeleteOutlined } from '@ant-design/icons';
import { ProductDoc, ProductInventoryDoc, ProductInventoryUnit, ProductSupplierDoc } from '@gooduncles/gu-app-schema';
import { Modal, Space, Tabs, message } from 'antd';
import { FC, ReactNode, useCallback } from 'react';
import { updateProductInventory } from 'src/utils/firebase/firebase-short-cut';
import { sortUomUnit } from 'src/utils/product-inventory-util';

import { ProductInventoryWithDetail } from 'src/hooks/subscription/useProductInventoriesWithDetail';

import InventoryDetail from 'src/atomic-components/molecules/InventoryDetail/InventoryDetail';

interface InventoryDetailBoardProps {
  inventory: ProductInventoryDoc;
  products: ProductDoc[];
  onTabChange: (tab: string) => void;
  tab: string;
  productSuppliers: ProductSupplierDoc[];
  inventories?: ProductInventoryWithDetail[]; // 모든 인벤토리 목록
}

const InventoryDetailBoard: FC<InventoryDetailBoardProps> = ({
  inventory,
  products,
  tab,
  onTabChange,
  productSuppliers,
  inventories = [],
}) => {
  const handleDeleteUnit = useCallback(
    async (unit: string, e?: React.MouseEvent) => {
      // 이벤트 버블링 방지
      if (e) {
        e.stopPropagation();
      }

      Modal.confirm({
        title: '단위 삭제',
        content: `${unit.toUpperCase()} 단위를 삭제하시겠습니까?`,
        okText: '삭제',
        okType: 'danger',
        cancelText: '취소',
        onOk: async () => {
          try {
            const updatedUom = { ...inventory.uom };
            if (unit in updatedUom) {
              const newUom = { ...updatedUom };
              delete newUom[unit as keyof typeof newUom];

              await updateProductInventory(inventory._id, { uom: newUom });
              message.success(`${unit.toUpperCase()} 단위가 삭제되었습니다.`);

              // 삭제된 탭이 현재 선택된 탭이면 다른 탭으로 이동
              if (tab === unit && Object.keys(newUom).length > 0) {
                onTabChange(Object.keys(newUom)[0]);
              }
            }
          } catch (error) {
            message.error('단위 삭제에 실패했습니다.');
            console.error(error);
          }
        },
      });
    },
    [inventory.uom, inventory._id, tab, onTabChange]
  );

  // 탭 라벨을 커스터마이징하는 함수
  const renderTabLabel = (unit: string, showDeleteButton: boolean): ReactNode => {
    return (
      <Space>
        {unit.toUpperCase()}
        {showDeleteButton && <DeleteOutlined style={{ color: 'red' }} onClick={(e) => handleDeleteUnit(unit, e)} />}
      </Space>
    );
  };

  return (
    <Tabs
      type='card'
      defaultActiveKey={tab}
      activeKey={tab}
      onChange={onTabChange}
      tabBarStyle={{ marginBottom: 0 }}
      items={sortUomUnit(Object.keys(inventory.uom)).map((unit) => {
        const product = products.find((product) => product.inventoryUnit === unit);
        return {
          key: unit,
          label: renderTabLabel(unit, !product && unit === 'bundle'),
          children: (
            <InventoryDetail
              inventory={inventory}
              unit={unit as ProductInventoryUnit}
              product={product}
              inventories={inventories}
              productSuppliers={productSuppliers}
            />
          ),
        };
      })}
    />
  );
};

export default InventoryDetailBoard;
